// The focus tool (slice 13; design/08 §5): the mode in which a drag on the
// board draws a box of squares and a click toggles one, and the draft those
// gather — held outside React like the selection, because the canvas is
// driven outside the render loop and the composer over the board is not.
// Setting the focus is the composer's; the tool only collects squares.
import { cellsInBox, toggleCell, type Cell } from '@dragons-table/vtt-shared';
import { wallTool } from './wallTool';
import { measureTool } from './measureTool';
import { targetTool } from './targetTool';
import { areaTool } from './areaTool';
import { fogTool } from './fogTool';
import { zoneTool } from './zoneTool';
import { drawTool } from './drawTool';
import { whatsThisTool } from './whatsThisTool';

export interface FocusDraft {
  /** Whether the board is in pointing mode. */
  on: boolean;
  cells: Cell[];
}

type Listener = (d: FocusDraft) => void;
let draft: FocusDraft = { on: false, cells: [] };
const listeners = new Set<Listener>();
const set = (next: FocusDraft) => { draft = next; for (const l of listeners) l(draft); };

export const focusTool = {
  get: () => draft,
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  },
  // One board mode at a time: pointing, measuring or laying an area (issue 832).
  start(): void { whatsThisTool.stop(); measureTool.stop(); areaTool.stop(); targetTool.stop(); fogTool.stop(); drawTool.stop(); wallTool.stop(); zoneTool.stop(); set({ on: true, cells: [] }); },
  stop(): void { if (draft.on || draft.cells.length) set({ on: false, cells: [] }); },
  toggle(): void { if (draft.on) focusTool.stop(); else focusTool.start(); },
  /** A click on one square adds it or removes it. */
  toggleCell(c: Cell): void { if (draft.on) set({ ...draft, cells: toggleCell(draft.cells, c) }); },
  /** A box adds every square inside it. */
  addBox(a: Cell, b: Cell): void {
    if (!draft.on) return;
    let cells = draft.cells;
    for (const c of cellsInBox(a, b)) if (!cells.some((x) => x.i === c.i && x.j === c.j)) cells = [...cells, c];
    set({ ...draft, cells });
  },
  clearCells(): void { set({ ...draft, cells: [] }); },
};
